/**
 * Decodes the exchange name from the API to a usable format
 * @param exchangeName
 * @returns
 */
export function decodeExchangeName(exchangeName: string) {
  if (exchangeName.includes("Pro") && !exchangeName.includes(" Pro")) {
    return exchangeName.substring(0, exchangeName.indexOf("Pro")) + " Pro";
  }
  return exchangeName;
}

/**
 * Encodes the exchange in order to send it to the API
 * @param exchangeName
 * @returns
 */
export function encodeExchangeName(exchangeName: string) {
  return exchangeName.split(" ").join("");
}

// 🗻 The proxy IP addresses used in order to make exchange calls
export const PROXY_IPS: string[] = [
  "51.15.114.83",
  "51.15.124.201",
  "51.158.112.184",
  "51.158.169.166",
  "51.159.151.141",
];

/**
 * @param exchange
 * @returns the formatted proxy IPs ready to be copied by the user
 */
export function formatWhitelistedIPsForExchange(
  exchangeName: string
): string | null {
  switch (exchangeName) {
    case "Binance":
      return PROXY_IPS.join(" ");
    case "Binance.US":
      return PROXY_IPS.join(" ");
    case "Kraken":
      return PROXY_IPS.join(",")
    case "Bitvavo":
      return PROXY_IPS.join(", ");
    case "Bitpanda Pro":
      return PROXY_IPS.join(", ");
    case "Coinbase Pro":
      return PROXY_IPS.join(", ");
    case "Crypto.com":
      return PROXY_IPS.join(" ");
    case "FTX":
      return null; //PROXY_IPS.join(" ");
    default:
      return null;
  }
}

export const PARTNERING_EXCHANGES: string[] = ["Binance", "Bitpanda Pro"];

export const EXCHANGES_WEBSITES: any = {
  Binance: "https://www.binance.com",
  "Binance.US": "https://www.binance.us",
  Kraken: "https://www.kraken.com/",
  Bitvavo: "https://bitvavo.com",
  "Bitpanda Pro": "https://pro.bitpanda.com",
  "Coinbase Pro": "https://pro.coinbase.com/",
  "Crypto.com": "https://crypto.com/exchange",
  FTX: "https://ftx.com/",
};

export const EXCHANGES_STABLE_COINS: any = {
  Binance: "USDT and BUSD", // TODO: 02/05/2022 Add USDC when lite view can use it for portfolio creation
  "Binance.US": "USD and USDT",
  Kraken: "USD", // TODO: 02/05/2022 Add USDT when lite view can use it for portfolio creation
  Bitvavo: "EUR",
  "Bitpanda Pro": "EUR",
  "Coinbase Pro": "USDT",
  "Crypto.com": "USDT and USDC",
  FTX: "USD",
};

export const EXCHANGES_SUBACCOUNT_SUPPORT: any = {
  Binance: false,
  "Binance.US": false,
  Kraken: false,
  Bitvavo: false,
  "Bitpanda Pro": false,
  "Coinbase Pro": false,
  "Crypto.com": false,
  FTX: true,
};

export const EXCHANGES_CONNECTION_FIELDS: any = {
  "Binance": { apiKey: true, apiSecret: true },
  "Kraken": { apiKey: true, apiSecret: true },
  "Binance.US": { apiKey: true, apiSecret: true },
  "Bitpanda Pro": { accessToken: true },
  "Bitvavo": { apiKey: true, apiSecret: true },
  "Coinbase Pro": { apiKey: true, apiSecret: true, apiPassPhrase: true },
  "Crypto.com": { apiKey: true, apiSecret: true },
  "FTX": { apiKey: true, apiSecret: true }
}


export const EXCHANGES_CONNECTION_STEPS: any = {
  Binance: {
    permissions: ["Enable Reading", "Enable Spot & Margin Trading"],
    pages: [
      {
        image: "binance-step-1",
        title: "Create a new API key",
        text:
          "<ol><li><a href='https://www.binance.com/en/my/settings/api-management' target='_blank' style='color:#e8b844'>Go to Binance's API management page <span class='mdi mdi-exit-to-app'/></a></li><li>Click the 'Create API' button on the API management page</li><li>Set an 'API label' such as 'One Click Crypto'</li><li>Complete the needed two-factor authentication (2FA)</li><li>Click the 'Submit' button</li></ol>",
      },
      {
        image: "binance-step-2",
        title: "Connect your newly created API key to One Click Crypto",
        text:
          "<ol><li>Enable the following permissions on your newly created API key:</li></ol>",
        showPermissions: true,
        ipAddressesText:
          "<ol start='2'><li>Enable the 'Restrict access to trusted IPs only' field on your API key</li><li>Copy and paste the following trusted IPs into the field:</li></ol>",
        showTrustedIPs: true,
        extraText:
          "<ol start='4'><li>Copy the generated Binance 'API Key' and 'Secret Key' into the form below</li><li>Click 'Connect Exchange' button in order to securely link your exchange account to One Click Crypto</li><li>That's it! You can start investing using Binance</li></ol>",
        showConnectExchangeForm: true,
      },
    ],
  },
  "Binance.US": {
    permissions: ["Enable Reading", "Enable Spot & Margin Trading"],
    pages: [
      {
        image: "binance-us-step-1",
        title: "Create a new API key",
        text:
          "<ol><li><a href='https://www.binance.us/en/settings/api-management' target='_blank' style='color:#e8b844'>Go to Binance.US's API management page <span class='mdi mdi-exit-to-app'/></a></li><li>Click the 'Create API' button</li><li>Set an 'API label' such as 'One Click Crypto'</li><li>Complete the needed two-factor authentication (2FA)</li><li>Click the 'Submit' button</li></ol>",
      },
      {
        image: "binance-us-step-2",
        title: "Connect your newly created API key to One Click Crypto",
        text:
          "<ol><li>Enable the following permissions on your newly created API key:</li></ol>",
        showPermissions: true,
        extraText:
          "<ol start='2'><li>Copy the generated Binance 'API Key' and 'Secret Key' into the form below</li><li>Click 'Connect Exchange' button in order to securely link your exchange account to One Click Crypto</li><li>That's it! You can start investing using Binance</li></ol>",
        showConnectExchangeForm: true,
      },
    ],
  },
  Kraken: {
    permissions: [
      "Query Funds",
      "Query Open Orders & Trades",
      "Query Closed Orders & Trades",
      "Create And Modify Orders",
      "Cancel/Close Orders",
      "Access WebSockets API",
    ],
    pages: [
      {
        image: "kraken-step-1",
        title: "Create a new API key",
        text:
          "<ol><li><a href='https://www.kraken.com/u/security/api' target='_blank' style='color:#e8b844'>Go to Kraken's API management page <span class='mdi mdi-exit-to-app'/></a></li><li>Click 'Generate New Key' / 'Add Key' button</li><li>Set a 'Key description' such as 'One Click Crypto'</li><li>Enable the following permissions for your new API key:</li></ol>",
        showPermissions: true,
        extraText:
          "<ol start='5'><li>Click the 'Generate key' button</li></ol>",
      },
      {
        stepTitle: "Connect API Credentials",
        image: "kraken-step-2",
        title: "Connect your newly created API to One Click Crypto",
        text:
          "<ol><li>Copy the generated Kraken 'API Key' and 'Secret Key' into the form below</li><li>Click 'Connect Exchange' button in order to securely link your exchange account to One Click Crypto</li><li>That's it! You can start investing using Kraken</li></ol>",
        showConnectExchangeForm: true,
      },
    ],
  },
  Bitvavo: {
    permissions: ["View Information", "Trade Digital Assets"],
    pages: [
      {
        image: "bitvavo-step-1",
        title: "Create a new API key",
        text:
          "<ol><li><a href='https://account.bitvavo.com/login' target='_blank' style='color:#e8b844'>Login into your Bitvavo account <span class='mdi mdi-exit-to-app'/></a></li><li><a href='https://account.bitvavo.com/user/api' target='_blank' style='color:#e8b844'>Go to Bitvavo's API management page <span class='mdi mdi-exit-to-app'/></a></li><li>Click the 'Request new API key' button</li><li>Set a 'Name' such as 'One Click Crypto'</li><li>Enable the following permissions for your new API key:</li></ol>",
        showPermissions: true,
        extraText:
          "<ol start='6'><li>Complete the needed two-factor authentication (2FA)</li><li>Click the 'Confirm' button</li><li>Confirm the API key creation on your email address</li></ol>",
      },
      {
        image: "bitvavo-step-2",
        title: "Connect your newly created API key to One Click Crypto",
        text:
          "<ol><li>Copy the generated Bitvavo 'API key' and 'API secret' into the form below</li><li>Click 'Connect Exchange' button in order to securely link your exchange account to One Click Crypto</li><li>That's it! You can start investing using Bitvavo</li></ol>",
        showConnectExchangeForm: true,
      },
    ],
  },
  "Bitpanda Pro": {
    permissions: ["Trade", "Read"],
    pages: [
      {
        image: "bitpandapro-step-1",
        title: "Create a new API key",
        text:
          "<ol><li><a href='https://account.bitpanda.com/login' target='_blank' style='color:#e8b844'>Login into your Bitpanda Pro account <span class='mdi mdi-exit-to-app'/></a></li><li><a href='https://exchange.bitpanda.com/account/keys' target='_blank' style='color:#e8b844'>Go to Bitpanda Pro's API management page <span class='mdi mdi-exit-to-app'/></a></li><li>Click the 'New API key' button</li><li>Set an 'API key name' such as 'One Click Crypto'</li><li>Enable the following permissions for your new API key:</li></ol>",
        showPermissions: true,
        extraText:
          "<ol start='6'><li>Click the 'Create API key' button</li><li>Complete the needed two-factor authentication (2FA)</li><li>Click the 'Authorise now' button</li><li>Confirm the API key creation on your email address</li></ol>",
      },
      {
        image: "bitpandapro-step-2",
        title: "Connect your newly created API key to One Click Crypto",
        text:
          "<ol><li>Copy the Bitpanda Pro 'API key' into the form below</li><li>Click 'Connect Exchange' button in order to securely link your exchange account to One Click Crypto</li><li>That's it! You can start investing using Bitpanda Pro</li></ol>",
        showConnectExchangeForm: true,
      },
    ],
  },
  "Coinbase Pro": {
    permissions: ["View", "Trade"],
    pages: [
      {
        image: "coinbasepro-step-1",
        title: "Create a new API key",
        text:
          "<ol><li><a href='https://pro.coinbase.com/profile/api' target='_blank' style='color:#e8b844'>Go to Coinbase Pro's API management page <span class='mdi mdi-exit-to-app'/></a></li><li>Click the '+ New API key' button</li><li>Set an 'API key nickname' such as 'One Click Crypto'</li><li>Enable the following permissions for your new API key:</li></ol>",
        showPermissions: true,
        extraText:
          "<ol start='6'><li>Click the 'Create API key' button</li><li>Complete the needed two-factor authentication (2FA)</li><li>Click the 'Add An API Key' button</li></ol>",
      },
      {
        image: "coinbasepro-step-2",
        title: "Connect your newly created API key to One Click Crypto",
        text:
          "<ol><li>Copy the Coinbase Pro 'API Secret' into the form below</li><li>Click 'Connect Exchange' button in order to securely link your exchange account to One Click Crypto</li><li>That's it! You can start investing using Coinbase Pro</li></ol>",
        showConnectExchangeForm: true,
      },
    ],
  },
  "Crypto.com": {
    permissions: ["Can Read", "Enable Trading"],
    pages: [
      {
        image: "crypto-com-step-1",
        title: "Create a new API key",
        text:
          "<ol><li><a href='https://crypto.com/exchange/user/settings/api-management' target='_blank' style='color:#e8b844'>Go to Crypto.com's API management page <span class='mdi mdi-exit-to-app'/></a></li><li>Click the 'Create a new API key' button</li><li>Set a 'Label' such as 'One Click Crypto'</li><li>Enter the needed two-factor authentication code (2FA)</li><li>Click the 'Create API' button</li></ol>",
      },
      {
        image: "crypto-com-step-2",
        title: "Connect your newly created API key to One Click Crypto",
        text:
          "<ol><li>Enable the following permissions for your new API key:</li></ol>",
        showPermissions: true,
        extraText:
          "<ol start='2'><li>Copy the Crypto.com 'API Key' and 'Secret Key' into the form below</li><li>Click the 'Save' button on 'Crypto.com'</li><li>Click the 'Connect Exchange' button in order to securely link your exchange account to One Click Crypto</li><li>That's it! You can start investing using Crypto.com</li></ol>",
        showConnectExchangeForm: true,
      },
    ],
  },
  FTX: {
    permissions: ["Trading"],
    pages: [
      {
        image: "ftx-step-1",
        title: "Create a new API key",
        text:
          "<ol><li><a href='https://ftx.com/settings/api' target='_blank' style='color:#e8b844'>Go to FTX's API management page <span class='mdi mdi-exit-to-app'/></a></li><li>Click the 'Create API key' button</li></ol>",
      },
      {
        image: "ftx-step-2",
        title: "Enter your two-factor authentication (2FA)",
        text:
          "<ol><li>Enter the needed two-factor authentication code (2FA)</li><li>Click the 'Submit' button</li></ol>",
      },
      {
        image: "ftx-step-3",
        title: "Connect your newly created API key to One Click Crypto",
        text:
          "<ol><li>Copy the FTX 'API Key' and 'API Secret' into the form below</li><li>Click 'Connect Exchange' button in order to securely link your exchange account to One Click Crypto</li><li>Make sure the following permissions are enabled for your new API key:</li></ol>",
        showPermissions: true,
        extraText:
          "<ol start='4'><li>That's it! You can start investing using FTX</li></ol>",
        showConnectExchangeForm: true,
      },
    ],
  },
};
